/**
 * Vendor form defaults — shared by `new.tsx` and `$vendorId.edit.tsx`.
 *
 * Same pattern as `features/customers/lib/form-defaults.ts`: RHF
 * controlled inputs default to `''`; the submit handler strips empty
 * optionals before posting so the wire stays lean (no `phone: ''` rows
 * that the contract would reject as a phoneIN regex mismatch).
 *
 * Bank-detail fields are deliberately absent from this shape — they
 * route through `vendors.changeBankDetails` (`stepUpFounder`,
 * ADR-0008), not the regular create/edit form.
 */
import type { vendors } from "@flint/api-contract";
import type { z } from "zod";

/**
 * Form-side value shape. Strings are always non-undefined (RHF wants
 * controlled inputs); the submit handler coerces back to `undefined` /
 * `null` per field before posting. The 12 V1.1 extension fields land
 * alongside the V1.0 baseline (migration 0040).
 */
export interface VendorFormValues {
  // V1.0 baseline
  name: string;
  contactName: string;
  gstin: string;
  phone: string;
  email: string;
  addressLine1: string;
  addressLine2: string;
  state: string;
  city: string;
  pincode: string;
  tdsSection: string;
  isJobWorker: boolean;
  paymentTerms: string;

  // V1.1 extensions (migration 0040)
  displayName: string;
  registrationType: VendorRegistrationType;
  contactRole: string;
  phoneWhatsapp: boolean;
  defaultPaymentMode: VendorDefaultPaymentMode | "";
  rcmDefault: boolean;
  itcEligible: boolean;
  msmeRegistered: boolean;
  vendorCategory: string;
  defaultGlAccount: string;
  tags: string[];
  internalNote: string;
}

export type VendorRegistrationType = z.infer<typeof vendors.vendorRegistrationType>;
export type VendorDefaultPaymentMode = z.infer<typeof vendors.vendorDefaultPaymentMode>;

export const EMPTY_FORM: VendorFormValues = {
  name: "",
  contactName: "",
  gstin: "",
  phone: "",
  email: "",
  addressLine1: "",
  addressLine2: "",
  state: "",
  city: "",
  pincode: "",
  tdsSection: "",
  isJobWorker: false,
  paymentTerms: "",

  displayName: "",
  registrationType: "regular",
  contactRole: "",
  phoneWhatsapp: false,
  defaultPaymentMode: "",
  rcmDefault: false,
  itcEligible: true,
  msmeRegistered: false,
  vendorCategory: "",
  defaultGlAccount: "",
  tags: [],
  internalNote: "",
};

type VendorRow = z.infer<typeof vendors.vendorRow>;

export function formFromVendor(v: VendorRow): VendorFormValues {
  return {
    name: v.name,
    contactName: v.contactName ?? "",
    gstin: v.gstin ?? "",
    phone: v.phone ?? "",
    email: v.email ?? "",
    addressLine1: v.addressLine1 ?? "",
    addressLine2: v.addressLine2 ?? "",
    state: v.state ?? "",
    city: v.city ?? "",
    pincode: v.pincode ?? "",
    tdsSection: v.tdsSection ?? "",
    isJobWorker: v.isJobWorker,
    paymentTerms: v.paymentTerms ?? "",

    displayName: v.displayName ?? "",
    registrationType: v.registrationType,
    contactRole: v.contactRole ?? "",
    phoneWhatsapp: v.phoneWhatsapp,
    defaultPaymentMode: v.defaultPaymentMode ?? "",
    rcmDefault: v.rcmDefault,
    itcEligible: v.itcEligible,
    msmeRegistered: v.msmeRegistered,
    vendorCategory: v.vendorCategory ?? "",
    defaultGlAccount: v.defaultGlAccount ?? "",
    tags: v.tags ?? [],
    internalNote: v.internalNote ?? "",
  };
}

export function valuesToCreatePayload(v: VendorFormValues): z.infer<typeof vendors.createInput> {
  const payload: z.infer<typeof vendors.createInput> = { name: v.name.trim() };
  if (v.contactName.trim()) payload.contactName = v.contactName.trim();
  if (v.gstin.trim()) payload.gstin = v.gstin.trim().toUpperCase();
  if (v.phone.trim()) payload.phone = v.phone.trim();
  if (v.email.trim()) payload.email = v.email.trim();
  if (v.addressLine1.trim()) payload.addressLine1 = v.addressLine1.trim();
  if (v.addressLine2.trim()) payload.addressLine2 = v.addressLine2.trim();
  if (v.state) payload.state = v.state as z.infer<typeof vendors.createInput>["state"];
  if (v.city.trim()) payload.city = v.city.trim();
  if (v.pincode.trim()) payload.pincode = v.pincode.trim();
  if (v.tdsSection.trim()) payload.tdsSection = v.tdsSection.trim();
  if (v.isJobWorker) payload.isJobWorker = true;
  if (v.paymentTerms.trim()) payload.paymentTerms = v.paymentTerms.trim();

  // V1.1 extension fields. Strings drop when empty (so the server's
  // optional() clause kicks in); booleans flow through unconditionally
  // because the server defaults differ from the form's "off" value;
  // tags is an array — send the trimmed/deduped list (server caps at 32).
  if (v.displayName.trim()) payload.displayName = v.displayName.trim();
  if (v.registrationType !== "regular") payload.registrationType = v.registrationType;
  if (v.contactRole.trim()) payload.contactRole = v.contactRole.trim();
  if (v.phoneWhatsapp) payload.phoneWhatsapp = true;
  if (v.defaultPaymentMode) payload.defaultPaymentMode = v.defaultPaymentMode;
  if (v.rcmDefault) payload.rcmDefault = true;
  if (!v.itcEligible) payload.itcEligible = false; // server default is true
  if (v.msmeRegistered) payload.msmeRegistered = true;
  if (v.vendorCategory.trim()) payload.vendorCategory = v.vendorCategory.trim();
  if (v.defaultGlAccount.trim()) payload.defaultGlAccount = v.defaultGlAccount.trim();
  const cleanedTags = dedupeTags(v.tags);
  if (cleanedTags.length > 0) payload.tags = cleanedTags;
  if (v.internalNote.trim()) payload.internalNote = v.internalNote.trim();
  return payload;
}

export function valuesToUpdatePayload(
  v: VendorFormValues,
  base: VendorFormValues,
  id: string,
): z.infer<typeof vendors.updateInput> {
  const patch: z.infer<typeof vendors.updateInput> = { id };
  if (v.name.trim() !== base.name.trim()) patch.name = v.name.trim();

  // ── String + enum fields: diff trimmed values ──────────────────
  const stringFields = [
    "contactName",
    "gstin",
    "phone",
    "email",
    "addressLine1",
    "addressLine2",
    "state",
    "city",
    "pincode",
    "tdsSection",
    "paymentTerms",
    "displayName",
    "contactRole",
    "vendorCategory",
    "defaultGlAccount",
    "internalNote",
  ] as const satisfies ReadonlyArray<keyof VendorFormValues>;

  for (const k of stringFields) {
    const nextRaw = v[k] as string;
    const prevRaw = base[k] as string;
    const next = k === "gstin" ? nextRaw.trim().toUpperCase() : nextRaw.trim();
    const prev = k === "gstin" ? prevRaw.trim().toUpperCase() : prevRaw.trim();
    if (next === prev) continue;
    if (k === "state") {
      if (next) (patch as Record<string, unknown>)[k] = next;
      continue;
    }
    (patch as Record<string, unknown>)[k] = next;
  }

  // ── Enum-string fields with no trim ('' represents "no override") ──
  if (v.registrationType !== base.registrationType) {
    (patch as Record<string, unknown>).registrationType = v.registrationType;
  }
  if (v.defaultPaymentMode !== base.defaultPaymentMode) {
    // Wire is enum | undefined; '' means "leave unset". Send the value
    // when non-empty; the server treats absent as no change so empty →
    // no patch is harmless.
    if (v.defaultPaymentMode) {
      (patch as Record<string, unknown>).defaultPaymentMode = v.defaultPaymentMode;
    }
  }

  // ── Boolean fields ────────────────────────────────────────────
  const booleanFields = [
    "phoneWhatsapp",
    "rcmDefault",
    "itcEligible",
    "msmeRegistered",
    "isJobWorker",
  ] as const satisfies ReadonlyArray<keyof VendorFormValues>;
  for (const k of booleanFields) {
    if (v[k] !== base[k]) (patch as Record<string, unknown>)[k] = v[k];
  }

  // ── Tags array ────────────────────────────────────────────────
  const nextTags = dedupeTags(v.tags);
  const prevTags = dedupeTags(base.tags);
  if (!sameStringArray(nextTags, prevTags)) {
    (patch as Record<string, unknown>).tags = nextTags;
  }

  return patch;
}

/** Trim, drop empties, dedupe (case-insensitive). Order preserved. */
function dedupeTags(tags: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const t of tags) {
    const trimmed = t.trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(trimmed);
  }
  return out;
}

function sameStringArray(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  for (let i = 0; i < sa.length; i++) if (sa[i] !== sb[i]) return false;
  return true;
}
